import { useState, useEffect } from 'react';
import {
    Loader2,
    RefreshCw,
    TrendingUp,
    Star,
    ArrowRight,
    Search as SearchIcon,
    Shield,
    BarChart3
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { getSavedContractIds } from '../services/favorites';

// Modular Components


export default function Dashboard() {
    const { appUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [recommendations, setRecommendations] = useState<any[]>([]);
    const [savedCount, setSavedCount] = useState(0);

    useEffect(() => {
        loadDashboardData();
    }, [appUser]);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            // Load saved count
            if (appUser?.contractorId) {
                const savedIds = await getSavedContractIds(appUser.contractorId);
                setSavedCount(savedIds.size);
            }

            // Load recent high-fit contracts (mock for now, or real query if we had a scans collection)
            const q = query(
                collection(db, 'contracts'),
                where('fitScore', '>=', 70),
                firestoreLimit(5)
            );
            const snapshot = await getDocs(q);
            const highFit = snapshot.docs.map(doc => doc.data());
            setRecommendations(highFit);

        } catch (err) {
            console.error('Error loading dashboard:', err);
        } finally {
            setLoading(false);
        }
    };

    const getCurrentDate = () => new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-blue-500/30">
            <div className="max-w-[1600px] mx-auto space-y-8 p-6 pb-20 fade-in">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight text-white flex items-center gap-3">
                            Command Center
                            <Shield className="w-6 h-6 text-blue-500" />
                        </h1>
                        <p className="text-gray-500 text-sm mt-1">{getCurrentDate()} • Welcome back, {appUser?.displayName || 'Agent'}</p>
                    </div>

                    <div className="flex items-center gap-3">
                        <Link to="/search" className="btn btn-primary shadow-lg shadow-blue-900/40">
                            <SearchIcon className="w-4 h-4" />
                            Launch Discovery
                        </Link>
                        <button onClick={loadDashboardData} className="p-2.5 bg-white/5 hover:bg-white/10 text-gray-400 rounded-xl border border-white/10 transition-all">
                            <RefreshCw className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* KPI Overview */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="card p-6 border-white/5 bg-gradient-to-br from-blue-500/5 to-transparent">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-blue-500/10 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-blue-400" />
                            </div>
                            <span className="text-[10px] font-mono text-blue-500 uppercase tracking-widest">Active Scan</span>
                        </div>
                        <p className="text-3xl font-bold text-white mb-1">84%</p>
                        <p className="text-xs text-gray-500">Market Fit Efficiency</p>
                    </div>

                    <div className="card p-6 border-white/5 bg-gradient-to-br from-purple-500/5 to-transparent">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-purple-500/10 rounded-lg">
                                <Star className="w-5 h-5 text-purple-400" />
                            </div>
                            <span className="text-[10px] font-mono text-purple-500 uppercase tracking-widest">Targeting</span>
                        </div>
                        <p className="text-3xl font-bold text-white mb-1">{savedCount}</p>
                        <p className="text-xs text-gray-500">Watching for Opportunities</p>
                    </div>

                    <div className="card p-6 border-white/5 bg-gradient-to-br from-green-500/5 to-transparent">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-green-500/10 rounded-lg">
                                <BarChart3 className="w-5 h-5 text-green-400" />
                            </div>
                            <span className="text-[10px] font-mono text-green-500 uppercase tracking-widest">Analytics</span>
                        </div>
                        <p className="text-3xl font-bold text-white mb-1">12</p>
                        <p className="text-xs text-gray-500">Strategic Analysts Online</p>
                    </div>

                    <div className="card p-6 border-white/5 bg-gradient-to-br from-amber-500/5 to-transparent">
                        <div className="flex items-center justify-between mb-4">
                            <div className="p-2 bg-amber-500/10 rounded-lg">
                                <Shield className="w-5 h-5 text-amber-400" />
                            </div>
                            <span className="text-[10px] font-mono text-amber-500 uppercase tracking-widest">System</span>
                        </div>
                        <p className="text-3xl font-bold text-white mb-1">Live</p>
                        <p className="text-xs text-gray-500">Next-Gen Intelligence Active</p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                    {/* Left Column: Recommendations */}
                    <div className="lg:col-span-8 space-y-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <TrendingUp className="w-5 h-5 text-blue-500" />
                                High-Confidence Targets
                            </h2>
                            <Link to="/search" className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1">
                                View Full Market <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>

                        <div className="space-y-4">
                            {recommendations.length > 0 ? recommendations.map((target, idx) => (
                                <Link
                                    key={idx}
                                    to={`/war-room/${target.noticeId}`}
                                    className="block card p-5 border-white/5 hover:border-blue-500/30 hover:bg-white/[0.02] transition-all group"
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2">
                                                <span className="px-2 py-0.5 rounded bg-blue-500 text-black text-[10px] font-black uppercase tracking-widest">
                                                    {target.fitScore}% FIT
                                                </span>
                                                <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest">{target.solicitationNumber}</span>
                                            </div>
                                            <h3 className="font-bold text-gray-200 group-hover:text-white transition-colors">
                                                {target.title}
                                            </h3>
                                            <p className="text-xs text-gray-500 line-clamp-1">{target.agency}</p>
                                        </div>
                                        <ArrowRight className="w-5 h-5 text-gray-700 group-hover:text-blue-500 transition-colors" />
                                    </div>
                                </Link>
                            )) : (
                                <div className="card p-12 text-center border-dashed border-white/10 bg-transparent">
                                    <div className="flex flex-col items-center gap-4">
                                        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
                                            <SearchIcon className="w-8 h-8 text-gray-600" />
                                        </div>
                                        <div>
                                            <p className="text-gray-400 font-medium">No intelligence gathered yet.</p>
                                            <p className="text-xs text-gray-600 mt-1">Run a market scan to see high-precision recommendations.</p>
                                        </div>
                                        <Link to="/search" className="btn btn-secondary text-xs">
                                            Begin Primary Search
                                        </Link>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Intelligence Stats & Actions */}
                    <div className="lg:col-span-4 space-y-6">
                        <div className="card p-6 border-white/5 bg-gradient-to-br from-blue-500/10 to-transparent">
                            <h3 className="text-sm font-bold mb-4 uppercase tracking-widest text-blue-400">System Capability</h3>
                            <div className="space-y-4">
                                <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Neural Link</span>
                                        <span className="text-[10px] font-mono text-blue-500">OPTIMAL</span>
                                    </div>
                                    <p className="text-xs text-blue-200/80 leading-relaxed">
                                        Direct connection to SAM.gov and FPDS-NG established. Real-time data sync active.
                                    </p>
                                </div>
                                <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/10">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Core AI</span>
                                        <span className="text-[10px] font-mono text-purple-500">GEMINI PRO 1.5</span>
                                    </div>
                                    <p className="text-xs text-purple-200/80 leading-relaxed">
                                        Multi-agent specialized analysis active for Technical, Compliance, and Pricing.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}



